import queryString from 'query-string';
import Router from 'next/router';

import config from '../utils/config';
import escapeExternalRedirectPath from '../utils/escapeExternalRedirectPath';
import {
  LOCAL_STORAGE_ORDER_STATUS_FAILED_ATTEMPTS_KEY,
  LOCAL_STORAGE_ORDER_STATUS_KEY,
} from '../utils/constants';
import { CLOSE_ORDER_HELP } from '../containers/OrderHelpTemplateContainer/templateTypes';
import { replaceUrlQueries } from '../utils/requestHelper';

import { isFeatureActive } from '../selectors/configSelectors';
import { CX_OH_DYNAMIC_ACTIONS_CALL_RIDER, OT_PUNCH_CARDS } from '../flags/features';

import {
  ORDER_HELP_ACTIONS,
  ORDER_HELP_FEEDBACK,
  ORDER_HELP_FEEDBACK_SCORE,
  ORDER_STATUS,
  ORDER_STATUS_FAILURE,
  LOCAL_STORAGE_ORDER_STATUS,
  LOCAL_STORAGE_ORDER_STATUS_FAILED_ATTEMPTS,
  ORDER_STATUS_TRACKING_DATA,
  ORDER_PUNCH_CARD,
  ORDER,
  ORDER_RATING,
  ORDER_RATING_DISMISS,
  ORDER_STATUS_WHATSAPP,
  RESET_INTERACTION,
  UPDATE_INTERACTION,
  SET_HELP_ORDER_ID,
  RELOAD_INTERACTION,
  SET_SPECIFIC_HELP_PAGE,
  SET_LIVE_CHAT_DEPARTMENT,
  CLOSE_INTERACTION_DIALOG,
  ORDER_STATUS_SHARE,
  INIT_ORDER_HELP,
  SET_ORDER_STATUS_SHARED,
  ORDER_HISTORY,
  LOAD_MORE_ORDER_HISTORY,
  ORDER_RECEIPT,
  HIDE_LOAD_MORE_ORDER_HISTORY,
  SET_ORDER_HELP_MODAL_IS_OPEN,
  SET_ORDER_HELP_MODAL_PAGE,
  SET_INFO_BANNER_MODAL_IS_OPEN,
} from './types';

import { trackOptedInForWhatsapp } from './tracking/orderStatus';

const headers = {
  'X-Roo-Supported-Templates': 'cancel_order, empty_state, actions_bottom_sheet, close_order_help',
};

export function initialiseOrderHelp(payload) {
  return {
    type: INIT_ORDER_HELP,
    payload,
  };
}

export function setOrderStatusShared(isShared) {
  return {
    type: SET_ORDER_STATUS_SHARED,
    payload: {
      isShared,
    },
  };
}

export function setHelpOrderId(orderId) {
  return {
    type: SET_HELP_ORDER_ID,
    payload: {
      orderId,
    },
  };
}

export function setIsSpecificHelpPage(isSpecificHelpPage) {
  return {
    type: SET_SPECIFIC_HELP_PAGE,
    payload: {
      isSpecificHelpPage,
    },
  };
}

export function setLiveChatDepartment(department) {
  return {
    type: SET_LIVE_CHAT_DEPARTMENT,
    payload: {
      department,
    },
  };
}

export function updateLiveChatDepartment() {
  return (dispatch, getState) => {
    const requestHost = getState().request.host;
    const templateData = getState().order.help.data;
    const liveChat = templateData && templateData.liveChat;
    const department = liveChat && liveChat.department;

    if (typeof window !== 'undefined' && department && requestHost) {
      window.parent.postMessage({ department }, requestHost);
    }
  };
}

export function setOrderHelpModalIsOpen(isOpen) {
  return {
    type: SET_ORDER_HELP_MODAL_IS_OPEN,
    payload: {
      isOpen,
    },
  };
}

export function setOrderHelpModalPage({
  selectedActionId,
  selectedActionTemplate,
  actionId,
  actionTemplate,
}) {
  return {
    type: SET_ORDER_HELP_MODAL_PAGE,
    payload: {
      selectedActionId,
      selectedActionTemplate,
      actionId,
      actionTemplate,
    },
  };
}

export function updateInteractionRoute(isServerSideRendered = false) {
  return (dispatch, getState) => {
    const { actionId, subType } = getState().order.help;

    if (!subType && !isServerSideRendered) {
      const { queries: newQueries, url: newUrl } = replaceUrlQueries(
        getState().request.currentUrl,
        { help: actionId },
        ['daai', 'daat', 'dasai', 'dasat'],
      );

      Router.push({ pathname: Router.pathname, query: newQueries }, newUrl, { shallow: true });
    }
  };
}

export function invokeTemplateAction() {
  return (dispatch, getState) => {
    switch (getState().order.help.type) {
      case CLOSE_ORDER_HELP: {
        const { iframe, redirectTo } = getState().order.help;

        if (typeof window !== 'undefined' && window.top.closeSelfHelp && iframe) {
          window.top.closeSelfHelp();
        } else {
          Router.push(escapeExternalRedirectPath(redirectTo));
        }

        return Promise.resolve();
      }
      default:
        return Promise.resolve();
    }
  };
}

export function resetInteraction() {
  return {
    type: RESET_INTERACTION,
  };
}

export function getInteractionActions(isServerSideRendered) {
  return (dispatch, getState) =>
    dispatch({
      type: UPDATE_INTERACTION,
      host: config.selfHelpServiceHost,
      endpoint: `/api/v2/interactions?order_id=${getState().order.help.id}`,
      options: {
        method: 'GET',
        headers,
      },
      onSuccess: () => {
        dispatch(updateLiveChatDepartment());
        dispatch(invokeTemplateAction());
        dispatch(updateInteractionRoute(isServerSideRendered));
      },
    });
}

export function startInteraction(
  parentActionId,
  parentActionTemplate,
  selectedActionId,
  selectedActionTemplate,
  isServerSideRendered,
) {
  return (dispatch, getState) => {
    const orderId = getState().order.help.id;

    return dispatch({
      type: UPDATE_INTERACTION,
      host: config.selfHelpServiceHost,
      endpoint: `/api/v2/interactions?order_id=${orderId}`,
      options: {
        method: 'POST',
        body: {
          order_id: orderId,
          action_id: parentActionId,
          action_template: parentActionTemplate,
          action_template_data: {
            selected_action_id: selectedActionId,
            selected_action_template: selectedActionTemplate,
          },
        },
        headers,
      },
      onSuccess: () => {
        dispatch(updateLiveChatDepartment());
        dispatch(invokeTemplateAction());
        dispatch(updateInteractionRoute(isServerSideRendered));
      },
    });
  };
}

export function updateInteraction(
  parentActionId,
  parentActionTemplate,
  templateData,
  isServerSideRendered,
) {
  return (dispatch, getState) => {
    const helpState = getState().order.help;

    const requestBody = {
      order_id: helpState.id,
      action_id: parentActionId,
      action_template: parentActionTemplate,
      action_template_data: templateData,
    };

    return dispatch({
      type: UPDATE_INTERACTION,
      host: config.selfHelpServiceHost,
      endpoint: `/api/v2/interactions/${helpState.interactionId}`,
      options: {
        method: 'PATCH',
        body: requestBody,
        headers,
      },
      onSuccess: () => {
        dispatch(updateLiveChatDepartment());
        dispatch(invokeTemplateAction());
        dispatch(updateInteractionRoute(isServerSideRendered));
      },
    });
  };
}

export function reloadInteraction(interactionData) {
  return (dispatch) =>
    dispatch({
      type: RELOAD_INTERACTION,
      payload: {
        interactionData,
      },
      onSuccess: () => {
        dispatch(updateLiveChatDepartment());
        dispatch(invokeTemplateAction());
        dispatch(updateInteractionRoute());
      },
    });
}

export function requestInteraction({
  pageActionId,
  parentActionId,
  parentActionTemplate,
  selectedActionId,
  selectedActionTemplate,
  templateData,
  isServerSideRendered,
} = {}) {
  return (dispatch, getState) => {
    const help = getState().order.help;

    // When the user goes back in browser history, check the interaction history
    if (help.interactionHistory && help.interactionHistory[pageActionId]) {
      return dispatch(reloadInteraction(help.interactionHistory[pageActionId]));
    }

    // If the user has an interaction, keep patching
    if (help.interactionId && parentActionId && parentActionTemplate && templateData) {
      return dispatch(
        updateInteraction(parentActionId, parentActionTemplate, templateData, isServerSideRendered),
      );
    }

    // If the user is starting interaction, post to the endpoint to get an interactionId
    if (parentActionId && parentActionTemplate && selectedActionId && selectedActionTemplate) {
      return dispatch(
        startInteraction(
          parentActionId,
          parentActionTemplate,
          selectedActionId,
          selectedActionTemplate,
          isServerSideRendered,
        ),
      );
    }

    // Default to getting the initial list of self-help actions
    return dispatch(getInteractionActions(isServerSideRendered));
  };
}

export function closeInteractionDialog() {
  return (dispatch) =>
    dispatch({
      type: CLOSE_INTERACTION_DIALOG,
    });
}

export function requestContactRiderHelpModal(orderId) {
  return (dispatch) => {
    const params = {
      order_id: orderId,
    };

    return dispatch({
      type: ORDER_HELP_ACTIONS,
      endpoint: `/orderapp/v1/help/actions/contact_rider?${queryString.stringify(params)}`,
    });
  };
}

export function loadNextFeedbackStep(action) {
  return (dispatch, getState) => {
    let { nextAction } = getState().order.helpFeedback;
    if (action && action.match(/[a-z_]+/)) {
      nextAction = action;
    }
    const params = {
      order_id: getState().order.help.id,
    };
    return dispatch({
      type: ORDER_HELP_FEEDBACK,
      endpoint: `/orderapp/v1/help/actions/${nextAction}?${queryString.stringify(params)}`,
    });
  };
}

export function setFeedbackDescription(description, url) {
  return (dispatch) =>
    dispatch({
      type: ORDER_HELP_FEEDBACK_SCORE,
      endpoint: `/orderapp/v1${url}`,
      options: {
        method: 'POST',
        body: {
          description,
        },
      },
      onSuccess: () => {
        dispatch(loadNextFeedbackStep());
      },
    });
}

export function hideLoadMoreOrderHistoryButton() {
  return (dispatch) =>
    dispatch({
      type: HIDE_LOAD_MORE_ORDER_HISTORY,
    });
}

export const ORDERS_PER_ORDER_HISTORY_PAGE = 25;

export function getOrderHistory() {
  return (dispatch, getState) => {
    const { unverifiedUserId } = getState().request;

    const currentPageIndex = getState().order.history.orderHistoryPage;
    const pageOffset = ORDERS_PER_ORDER_HISTORY_PAGE * currentPageIndex;

    if (unverifiedUserId) {
      return dispatch({
        type: ORDER_HISTORY,
        /* TODO: Remove + 1 from ORDERS_PER_ORDER_HISTORY_PAGE once we get proper pagination from API */
        endpoint: `/orderapp/v1/users/${unverifiedUserId}/orders?limit=${ORDERS_PER_ORDER_HISTORY_PAGE +
          1}&offset=${pageOffset}`,
        onSuccess: (response) => {
          /* TODO: Remove once we get proper pagination from API */
          if (response.payload.orders.length <= ORDERS_PER_ORDER_HISTORY_PAGE) {
            dispatch(hideLoadMoreOrderHistoryButton());
          }
        },
      });
    }

    return Promise.resolve();
  };
}

export function loadMoreOrderHistory() {
  return (dispatch) => {
    dispatch({
      type: LOAD_MORE_ORDER_HISTORY,
    });

    return dispatch(getOrderHistory());
  };
}

export function getOrderReceipt(orderId) {
  return (dispatch, getState) => {
    const { unverifiedUserId } = getState().request;

    if (unverifiedUserId && orderId) {
      return dispatch({
        type: ORDER_RECEIPT,
        endpoint: `/orderapp/v1/users/${unverifiedUserId}/orders/${orderId}`,
      });
    }

    return Promise.resolve();
  };
}

export function getOrderStatusFailedAttemptsFromLocalStorage() {
  return (dispatch, getState) => {
    const orderId = getState().request.currentQuery.orderId;
    const orderStatusesFailedAttempts = JSON.parse(
      window.localStorage.getItem(LOCAL_STORAGE_ORDER_STATUS_FAILED_ATTEMPTS_KEY),
    );
    const failedAttempts = orderStatusesFailedAttempts && orderStatusesFailedAttempts[orderId];

    if (!failedAttempts) {
      return Promise.resolve();
    }

    return dispatch({
      type: LOCAL_STORAGE_ORDER_STATUS_FAILED_ATTEMPTS,
      payload: {
        failedAttempts,
      },
    });
  };
}

export function setOrderStatusFailedAttemptsFromLocalStorage(orderId, failedAttempts = 0) {
  if (
    orderId &&
    typeof window !== 'undefined' &&
    window.localStorage &&
    typeof window.localStorage === 'object'
  ) {
    const localStorageOrderStatusesFailedAttempts =
      JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_ORDER_STATUS_FAILED_ATTEMPTS_KEY)) || {};

    let newlocalStorageOrderStatusesFailedAttempts = {};
    if (failedAttempts === 0) {
      const {
        [orderId]: removeThis,
        ...otherOrderStatusesFailedAttempts
      } = localStorageOrderStatusesFailedAttempts;
      newlocalStorageOrderStatusesFailedAttempts = otherOrderStatusesFailedAttempts;
    } else {
      newlocalStorageOrderStatusesFailedAttempts = {
        ...localStorageOrderStatusesFailedAttempts,
        [orderId]: failedAttempts,
      };
    }

    window.localStorage.setItem(
      LOCAL_STORAGE_ORDER_STATUS_FAILED_ATTEMPTS_KEY,
      JSON.stringify(newlocalStorageOrderStatusesFailedAttempts),
    );
  }
}

export function getOrderStatus(orderId, failedAttempts = 0) {
  return (dispatch, getState) => {
    const state = getState();
    const request = state.request;
    const params = {};
    const sharingToken = request.currentQuery ? request.currentQuery.sharing_token : null;

    if (sharingToken) {
      params.sharing_token = sharingToken;
    }

    if (typeof window !== 'undefined' && window.Intl && typeof window.Intl === 'object') {
      // Assume it's supported, lets localize!
      const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
      if (timeZone) {
        params.tz = timeZone;
      }
    }

    const query = queryString.stringify(params) ? `?${queryString.stringify(params)}` : '';
    return dispatch({
      type: ORDER_STATUS,
      host: config.orderStatusServiceHost,
      endpoint: `/api/v2-4/consumer_order_statuses/${orderId}${query}`,
      options: {
        headers: {
          'X-Roo-Failed-Attempts': failedAttempts,
        },
      },
      onSuccess: () => {
        const { contactDetails, order } = state.order.status;
        const orderHelpDynamicActionsCallRiderWebOn = isFeatureActive(
          state,
          CX_OH_DYNAMIC_ACTIONS_CALL_RIDER,
        );

        if (
          contactDetails &&
          contactDetails.showContactButton &&
          !contactDetails.modal &&
          !orderHelpDynamicActionsCallRiderWebOn
        ) {
          dispatch(requestContactRiderHelpModal(order.id));
        }
      },
    }).catch((error) => {
      /*
        Any client side API requests that error will be caught in this block.
        The error caught will be thrown in the `createExceptionHandler` function - app/middleware/api.js.
        On Order Status we always want to respond to a failed request by dispatching the FAILED action types.
      */

      const status = error.status || 500;
      const data = error.data || {};
      const message = data.message || error.message;

      dispatch({
        type: ORDER_STATUS_FAILURE,
        status,
        message,
        payload: {
          ...data,
        },
      });
    });
  };
}

export function getOrderStatusFromLocalStorage() {
  return (dispatch, getState) => {
    const orderId = getState().request.currentQuery.orderId;
    const orderStatuses = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_ORDER_STATUS_KEY));
    const orderStatus = orderStatuses && orderStatuses[orderId];

    if (getState().order.status.order || !orderStatus) {
      return Promise.resolve();
    }

    return dispatch({
      type: LOCAL_STORAGE_ORDER_STATUS,
      payload: orderStatus,
    });
  };
}

export function requestOrderStatusShare() {
  return (dispatch, getState) => {
    const order = getState().order.status.order;

    if (order.sharingEnabled) {
      return Promise.resolve();
    }

    return dispatch({
      type: ORDER_STATUS_SHARE,
      host: config.orderStatusServiceHost,
      endpoint: `/api/v2/consumer_order_statuses/${order.id}`,
      options: {
        method: 'PATCH',
      },
    });
  };
}

export function getOrderStatusTrackingData(orderId, isSharedOrder = false) {
  return (dispatch, getState) => {
    const { unverifiedUserId } = getState().request;

    if (!isSharedOrder && unverifiedUserId) {
      return dispatch({
        type: ORDER_STATUS_TRACKING_DATA,
        endpoint: `/orderapp/v1/users/${unverifiedUserId}/orders/${orderId}/tracking`,
      });
    }
    return Promise.resolve();
  };
}

export function getOrderPunchCard(orderId) {
  return (dispatch, getState) => {
    if (isFeatureActive(getState(), OT_PUNCH_CARDS)) {
      return dispatch({
        type: ORDER_PUNCH_CARD,
        host: config.orderStatusServiceHost,
        endpoint: `/api/punch_card/${orderId}`,
        payload: {
          orderId,
        },
      });
    }
    return Promise.resolve();
  };
}

export function getLastOrder() {
  return (dispatch, getState) => {
    const { unverifiedUserId } = getState().request;
    if (unverifiedUserId) {
      return dispatch({
        type: ORDER,
        endpoint: `/orderapp/v1/users/${unverifiedUserId}/orders?limit=1`,
      });
    }
    return Promise.resolve();
  };
}

export function setOrderRating(orderId, rating, issues, comment) {
  return (dispatch, getState) => {
    const { unverifiedUserId } = getState().request;

    return dispatch({
      type: ORDER_RATING,
      endpoint: `/orderapp/v1/users/${unverifiedUserId}/orders/${orderId}/rating`,
      options: {
        method: 'PATCH',
        body: {
          stars: rating,
          dismissed: false,
          issues,
          comment,
        },
      },
    });
  };
}

export function dismissRating(orderId) {
  return (dispatch, getState) => {
    const { unverifiedUserId } = getState().request;

    return dispatch({
      type: ORDER_RATING_DISMISS,
      endpoint: `/orderapp/v1/users/${unverifiedUserId}/orders/${orderId}/rating`,
      options: {
        method: 'PATCH',
        body: {
          dismissed: true,
        },
      },
    });
  };
}

export function setInfoBannerModalIsOpen(isOpen) {
  return {
    type: SET_INFO_BANNER_MODAL_IS_OPEN,
    payload: {
      isOpen,
    },
  };
}

export function optInToWhatsapp() {
  return (dispatch, getState) => {
    const { order } = getState().order.status;

    dispatch(trackOptedInForWhatsapp());

    return dispatch({
      type: ORDER_STATUS_WHATSAPP,
      host: config.orderStatusServiceHost,
      endpoint: `/api/whatsapp?order_id=${order.id}`,
      options: {
        method: 'POST',
      },
    });
  };
}
